"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

const ITEMS = [
	{ id: "vBending", titleKey: "vBendingTitle", descKey: "vBendingDesc" },
	{ id: "airBending", titleKey: "airBendingTitle", descKey: "airBendingDesc" },
	{ id: "bottoming", titleKey: "bottomingTitle", descKey: "bottomingDesc" },
	{ id: "coining", titleKey: "coiningTitle", descKey: "coiningDesc" },
	{ id: "uBending", titleKey: "uBendingTitle", descKey: "uBendingDesc" },
	{ id: "rollBending", titleKey: "rollBendingTitle", descKey: "rollBendingDesc" },
	{ id: "rotaryBending", titleKey: "rotaryBendingTitle", descKey: "rotaryBendingDesc" },
	{ id: "stepBending", titleKey: "stepBendingTitle", descKey: "stepBendingDesc" },
] as const;

type MethodId = (typeof ITEMS)[number]["id"];

export interface SheetMetalBendingMethodsProps {
	translationNamespace?: string;
	imageSrc: string;
}

/**
 * 左：标题 + 导语 + 图；右：边框手风琴。大屏两列顶部对齐。
 */
export default function SheetMetalBendingMethods({
	translationNamespace = "metalBendingMethods",
	imageSrc,
}: SheetMetalBendingMethodsProps) {
	const t = useTranslations(translationNamespace);
	const [openId, setOpenId] = useState<MethodId | null>(ITEMS[0].id);

	const toggle = (id: MethodId) => {
		setOpenId((prev) => (prev === id ? null : id));
	};

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-8 xl:gap-10">
					<div className="w-full min-w-0 flex-1 lg:min-w-0">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<p className="mt-3 text-sm leading-relaxed text-black sm:mt-4 md:text-base">{t("intro")}</p>
						<div className="relative mt-6 aspect-[5/3] w-full overflow-hidden shadow-lg sm:mt-8">
							<Image
								src={imageSrc}
								alt={t("imageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>

					<div className="w-full min-w-0 flex-1 self-start lg:min-w-0">
						<div className="overflow-hidden border border-gray-200 bg-white shadow-sm">
							{ITEMS.map((item, index) => {
								const isOpen = openId === item.id;
								const isLast = index === ITEMS.length - 1;
								return (
									<div key={item.id} className={!isLast ? "border-b border-gray-200" : ""}>
										<button
											type="button"
											onClick={() => toggle(item.id)}
											className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors duration-200 hover:bg-gray-50 sm:px-6 sm:py-5"
											aria-expanded={isOpen}
										>
											<span className="text-base font-bold text-black sm:text-lg">{t(item.titleKey)}</span>
											<span className="flex h-8 w-8 shrink-0 items-center justify-center text-themePrimary400">
												{isOpen ? (
													<CaretDown className="h-5 w-5" weight="bold" aria-hidden />
												) : (
													<CaretRight className="h-5 w-5" weight="bold" aria-hidden />
												)}
											</span>
										</button>
										<div
											className={`grid transition-all duration-300 ease-in-out ${
												isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
											}`}
										>
											<div className="min-h-0 overflow-hidden">
												<div className="px-4 pb-5 pt-0 text-sm leading-relaxed text-black sm:px-6 sm:pb-6 md:text-base">
													{t(item.descKey)}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
